import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../../app/errors';
import { log } from '../../app/logger';
import { closeRecovery, exportRecoveryArchive, readRecoveryOverview } from '../../app/recoveryExport';
import type { RecoveryOverview } from '../../app/recoveryExport';

/**
 * B-21 — the recovery screen's controller (design §13.5).
 *
 * The shape every B screen uses: one read on mount, a `reads` counter rendered as an attribute so a
 * browser pass waits on a number rather than a sleep, and everything the screen shows coming back
 * from a read rather than from the screen's belief about its own action.
 *
 * ── What is different here, and why it is deliberately less ─────────────────
 *
 * There is no `refresh`, because nothing on this screen changes anything to re-read. The database
 * cannot be written by this bundle at all (`data/db.ts`'s guard refuses every transaction against a
 * newer schema), the export is a read, and there is no repair, reset or delete on this surface —
 * §9's failure table says *"export + update; never 'repair'"* and §13.4 says why: forward-only
 * migrations make a rollback across a version bump data-destructive. A control that offered to fix
 * it would be the one control on this screen capable of losing the data it exists to save.
 */

const MODULE = 'ui-b/recovery';

type Phase = 'loading' | 'ready' | 'failed';

export interface RecoveryExportReport {
  readonly filename: string;
  readonly bytes: number;
  readonly blobs: number;
  readonly shared: boolean;
  /**
   * Setting keys that did not travel — §11.8's declared refusals **and** keys a newer build wrote
   * whose values this bundle will not publish. One list on the screen because the user's question
   * is the same for both ("what did not come with me?"); two lists in the file because the reasons
   * differ. Surfaced at all because a silent omission is the thing this unit exists against.
   */
  readonly withheldSettingKeys: readonly string[];
  /** Stores this bundle has no name for that rode along in the rescue entry. */
  readonly rescuedTables: readonly string[];
  /** Stores that could not be read at all. A partial rescue must say which part is missing. */
  readonly unreadableStores: readonly string[];
}

export interface RecoveryState {
  readonly phase: Phase;
  /** `null` until the read lands, and on failure. */
  readonly overview: RecoveryOverview | null;
  /** Why the read failed — shown, never swallowed (NFR-8). */
  readonly problem: string | null;
  readonly busy: boolean;
  readonly exported: RecoveryExportReport | null;
  readonly exportProblem: string | null;
  readonly reads: number;
}

export interface RecoveryActions {
  readonly runExport: (at: number) => void;
}

export function useRecovery() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [overview, setOverview] = useState<RecoveryOverview | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [exported, setExported] = useState<RecoveryExportReport | null>(null);
  const [exportProblem, setExportProblem] = useState<string | null>(null);
  const [reads, setReads] = useState(0);

  useEffect(() => {
    let live = true;
    void readRecoveryOverview()
      .then((next) => {
        if (!live) return;
        setOverview(next);
        setPhase('ready');
        log.warn(MODULE, '§13.5: the stored schema is newer than this bundle', {
          storedVersion: next.storedVersion,
          bundleVersion: next.bundleVersion,
          totalRows: next.totalRows,
          stores: next.storeNames,
        });
      })
      .catch((cause: unknown) => {
        if (!live) return;
        log.exception(MODULE, '§13.5: the database could not be read for recovery', cause);
        setProblem(sentence(cause));
        setPhase('failed');
      })
      .finally(() => {
        if (live) setReads((count) => count + 1);
      });

    return () => {
      live = false;
      // The recovery connection is this screen's, and it is the only thing holding it open.
      closeRecovery();
    };
  }, []);

  const runExport = useCallback((at: number): void => {
    setBusy(true);
    setExportProblem(null);
    setExported(null);
    void exportRecoveryArchive(at)
      .then((done) => {
        setExported({
          filename: done.filename,
          bytes: done.archive.bytes.length,
          blobs: done.archive.result.blobCount,
          shared: done.route === 'share-file',
          withheldSettingKeys: [
            ...done.archive.residue.withheldSettingKeys,
            ...done.archive.residue.unknownSettingKeys,
          ].sort(),
          rescuedTables: Object.keys(done.archive.residue.unknownTables).sort(),
          unreadableStores: done.archive.residue.unreadableStores,
        });
      })
      .catch((cause: unknown) => {
        // A cancelled share sheet already carries a sentence written for a user (`app/shareFile`),
        // so it is surfaced verbatim rather than re-worded here — the same rule the More tab takes.
        log.error(MODULE, '§13.5: the rescue archive was not saved', {
          message: errorMessage(cause),
        });
        setExportProblem(sentence(cause));
      })
      .finally(() => {
        setBusy(false);
      });
  }, []);

  return {
    state: {
      phase,
      overview,
      problem,
      busy,
      exported,
      exportProblem,
      reads,
    } satisfies RecoveryState,
    actions: { runExport } satisfies RecoveryActions,
  };
}

/** The sentence a user reads, without the error class name `errorMessage` prefixes for the ring. */
function sentence(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
